/**
 * TransmissionPreparationFlow.js
 * Parcours explicite de préparation d'une transmission (PF-03 CP2, D4 ;
 * CP3.1, DEC-037).
 *
 * Choix du chemin de remise du code → buildTransmission() → affichage du
 * code 24 mots → retape de 3 mots → commitTransmission(releasePath) →
 * étape optionnelle « Prévenir un proche ? » (CP3.2, DEC-038). L'enveloppe
 * est en base avant cette étape : fermer l'onglet ici ne perd rien.
 * L'enveloppe et le code ne vivent que dans le state de ce composant et sont
 * jetés au démontage (DEC-033 Q5) : onglet fermé avant confirmation = rien
 * en base. Une string JS ne peut pas être mise à zéro ; la garantie est
 * qu'elle n'est conservée nulle part ailleurs.
 */

import React, { useEffect, useState } from 'react';
import SeedDisplayScreen from './SeedDisplayScreen';
import SeedConfirmationScreen, { pickPositions } from './SeedConfirmationScreen';
import ReleasePathSelector from './ReleasePathSelector';
import TrustedContactForm, { CONTACT_SEND_FAILED_MESSAGE } from './TrustedContactForm';
import { BG, G, BtnBack, BtnPrimary, ErrBox } from './onboardingAtoms';
import { buildTransmission, commitTransmission } from '../services/transmissionService';
import { setTrustedContact } from '../services/beneficiaryService';
import { sendTrustedContactNotification } from '../services/emailService';
import { DEFAULT_RELEASE_PATH, getReleasePath } from '../config/releasePathConfig';

const WRAP = { maxWidth: 520, margin: '0 auto', padding: '1.5rem 1.5rem 3rem' };

const firstNameOf = (beneficiary) => (beneficiary.name || '').trim().split(/\s+/)[0] || 'votre bénéficiaire';

/** Chemin pré-sélectionné : celui du bénéficiaire s'il est actif, sinon le défaut. */
const initialPathOf = (beneficiary) =>
  getReleasePath(beneficiary.release_path)?.availability === 'active'
    ? beneficiary.release_path
    : DEFAULT_RELEASE_PATH;

const TransmissionPreparationFlow = ({ beneficiary, masterKey, onDone, onCancel }) => {
  const [step, setStep] = useState('path');
  const [chosenPath, setChosenPath] = useState(() => initialPathOf(beneficiary));
  const [prepared, setPrepared] = useState(null);
  const [positions, setPositions] = useState([]);
  const [error, setError] = useState('');
  const [contactBusy, setContactBusy] = useState(false);
  const [contactError, setContactError] = useState('');
  const [contactSendFailed, setContactSendFailed] = useState(false);

  useEffect(() => {
    if (step !== 'building') return undefined;
    let cancelled = false;
    (async () => {
      const result = await buildTransmission({ beneficiary, masterKey });
      if (cancelled) return;
      if (!result.success) {
        setError(result.error);
        setStep('error');
        return;
      }
      setPrepared({ envelopeRow: result.envelopeRow, holderMnemonic: result.holderMnemonic });
      setPositions(pickPositions());
      setStep('display');
    })();
    return () => { cancelled = true; };
  }, [step, beneficiary, masterKey]);

  const handleConfirm = async () => {
    setStep('committing');
    const result = await commitTransmission({
      beneficiary,
      envelopeRow: prepared.envelopeRow,
      releasePath: chosenPath,
    });
    // Enveloppe en base ou échec : dans les deux cas le code n'est plus affiché.
    setPrepared(null);
    if (result.success) {
      setStep('trusted_contact');
      return;
    }
    setError(result.error);
    setStep('error');
  };

  const handleNotifyContact = async (contact) => {
    setContactBusy(true);
    setContactError('');
    const saved = await setTrustedContact(beneficiary.id, contact, masterKey);
    if (!saved.success) {
      setContactError(saved.error);
      setContactBusy(false);
      return;
    }
    const sent = await sendTrustedContactNotification(beneficiary.id, contact);
    if (!sent) {
      setContactSendFailed(true);
      setContactBusy(false);
      return;
    }
    onDone();
  };

  const firstName = firstNameOf(beneficiary);
  const path = getReleasePath(chosenPath);

  return (
    <div style={{ minHeight: '100vh', background: BG, color: '#fff', paddingTop: 80 }}>
      <div style={WRAP}>
        {step === 'path' && (
          <>
            <BtnBack onClick={onCancel} />
            <h2 style={{ margin: '0 0 1.25rem', fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>
              Comment conserverez-vous le code de transmission pour {firstName} ?
            </h2>
            <ReleasePathSelector value={chosenPath} onChange={setChosenPath} />
            <div style={{ marginTop: 20 }}>
              <BtnPrimary onClick={() => setStep('building')} disabled={path?.availability !== 'active'}>
                Continuer
              </BtnPrimary>
            </div>
          </>
        )}

        {step === 'building' && (
          <p style={{ margin: 0, color: G, fontSize: '0.9rem' }}>Préparation de la transmission…</p>
        )}

        {step === 'error' && (
          <>
            <BtnBack onClick={onCancel}>← Retour à mes bénéficiaires</BtnBack>
            <ErrBox msg={error} />
          </>
        )}

        {step === 'display' && (
          <SeedDisplayScreen
            seedPhrase={prepared.holderMnemonic}
            title={`Code de transmission pour ${firstName}`}
            intro={path.storageInstruction(firstName)}
            printTitle={`SafeHeir — Code de transmission pour ${firstName}`}
            warning={`Ne prenez jamais ce code en photo, ne le stockez jamais dans un cloud, une note numérique ou un email. Sans ce code, ${firstName} ne pourra pas recevoir les données que vous lui destinez. SafeHeir n'en conserve aucune copie.`}
            acknowledgeLabel="J'ai imprimé ou noté ce code et je le conserve hors de mon coffre numérique."
            onContinue={() => setStep('confirm')}
            onBack={onCancel}
          />
        )}

        {step === 'confirm' && (
          <SeedConfirmationScreen
            seedPhrase={prepared.holderMnemonic}
            positions={positions}
            confirmLabel="Confirmer la préparation"
            onConfirm={handleConfirm}
            onBack={() => setStep('display')}
          />
        )}

        {step === 'committing' && (
          <p style={{ margin: 0, color: G, fontSize: '0.9rem' }}>Enregistrement de la transmission…</p>
        )}

        {step === 'trusted_contact' && (
          <>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>
              Prévenir un proche ?
            </h2>
            <p style={{ margin: '0 0 1.5rem', color: G, fontSize: '0.9rem', lineHeight: 1.6 }}>
              Un proche peut être informé qu'une transmission est préparée pour {firstName}, afin de
              savoir auprès de qui le code est conservé. Il ne recevra jamais le code ni aucune donnée
              du coffre.
            </p>
            {contactSendFailed ? (
              <>
                <ErrBox msg={CONTACT_SEND_FAILED_MESSAGE} />
                <div style={{ marginTop: 16 }}>
                  <BtnPrimary onClick={onDone}>Terminer</BtnPrimary>
                </div>
              </>
            ) : (
              <>
                {contactError && (
                  <div style={{ marginBottom: 16 }}>
                    <ErrBox msg={contactError} />
                  </div>
                )}
                <TrustedContactForm
                  submitLabel="Prévenir"
                  secondaryLabel="Passer"
                  onSubmit={handleNotifyContact}
                  onSecondary={onDone}
                  busy={contactBusy}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TransmissionPreparationFlow;
